import React from "react";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  PlayCircleFilled as PlayIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { getTranslation } from "common";
import MyActivitiesMobileFilters from "./MyActivitiesMobileFilters";
import { ProcessModelType } from "model/processModel";
import { Priority } from "hooks/usePriorities";
import { ServerTag } from "model/activitiesModel";
import { SortCriterion } from "common/utilities";
import { User } from "components/UserItem";

interface MyActivitiesMobileHeaderProps {
  searchText: string;
  setSearchText: (v: string) => void;
  onToggleFilters: () => void;
  /** Whether the filter section is currently open (drives the icon highlight). */
  filtersOpen: boolean;
  setStartProcess: (open: boolean) => void;
  liveProcessCanUse: boolean;
  bypassSubscription: boolean;
  organizationId?: number;
  organizationName?: string;
  translations: Record<string, string>;

  // Filter-related props (forwarded to MyActivitiesMobileFilters)
  searchTags: number[];
  setSearchTags: (tags: number[]) => void;
  selectedPriorities: string[];
  setSelectedPriorities: (priorities: string[]) => void;
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
  setSortCriteria: (criteria: SortCriterion) => void;
  handleClearFilters: () => void;
  processModels: ProcessModelType[];
  tagList: ServerTag[];
  priorities: Priority[];
  userDataKeys: string[];
  activeFilterCount: number;
  /** Creator filter — explicit list of user ids. */
  selectedCreatorIds: number[];
  setSelectedCreatorIds: (ids: number[]) => void;
  /** Org users used to populate the "Creato da" filter selector. */
  orgUsers: User[];
}

/**
 * Mobile compact header.
 * Left: Start (Avvia) button. Center: search input. Right: filter toggle.
 * When the filter section is open, the full filter panel is shown
 * directly below the search row — no quick-pills on top because the
 * filter group already covers every category (User Data, Tag,
 * Priority, Process Model, Sort).
 */
const MyActivitiesMobileHeader: React.FC<MyActivitiesMobileHeaderProps> = ({
  searchText,
  setSearchText,
  onToggleFilters,
  filtersOpen,
  setStartProcess,
  liveProcessCanUse,
  bypassSubscription,
  organizationId,
  organizationName,
  translations,
  searchTags,
  setSearchTags,
  selectedPriorities,
  setSelectedPriorities,
  selectedModels,
  setSelectedModels,
  selectedUserDataKey,
  handleSelectUserDataKey,
  setSortCriteria,
  handleClearFilters,
  processModels,
  tagList,
  priorities,
  userDataKeys,
  activeFilterCount,
  selectedCreatorIds,
  setSelectedCreatorIds,
  orgUsers,
}) => {
  const navigate = useNavigate();

  const startLabel = getTranslation(translations, "pm.startprocess.button.ok.caption");
  const searchPlaceholder = getTranslation(translations, "pm.organizationchart.ricerca.placeholder");
  const filterLabel = getTranslation(translations, "generic.filters.caption");
  const liveLimitLabel = getTranslation(translations, "live_processes.limit.reached");
  const upgradeLabel = getTranslation(translations, "generic.upgrade");

  const startDisabled = !bypassSubscription && !liveProcessCanUse;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <FlowentiButton
          id="mobile-startprocess-button"
          size="small"
          variant="contained"
          disabled={startDisabled}
          onClick={() => setStartProcess(true)}
          startIcon={<PlayIcon sx={{ fontSize: 16 }} />}
          sx={{
            minWidth: 0,
            px: 1.25,
            py: 0.5,
            fontSize: 11,
            fontWeight: 700,
            flexShrink: 0,
            borderRadius: 3,
          }}
        >
          {startLabel}
        </FlowentiButton>

        <TextField
          id="mobile-myactivities-search"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={searchPlaceholder}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              fontSize: 12,
              bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.100"),
              "& fieldset": { border: "none" },
              height: 32,
            },
            "& .MuiOutlinedInput-input": { py: 0.75, fontSize: 12 },
          }}
        />

        <Tooltip title={filterLabel} arrow>
          <IconButton
            id="mobile-myactivities-filter-toggle"
            size="small"
            onClick={onToggleFilters}
            aria-label={filterLabel}
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              bgcolor: filtersOpen
                ? "primary.main"
                : (t) => (t.palette.mode === "dark" ? "background.default" : "grey.100"),
              color: filtersOpen ? "common.white" : "text.secondary",
              "&:hover": {
                bgcolor: filtersOpen ? "primary.dark" : (t) => (t.palette.mode === "dark" ? "action.hover" : "grey.200"),
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={filtersOpen} unmountOnExit>
        <MyActivitiesMobileFilters
          searchText={searchText}
          setSearchText={setSearchText}
          searchTags={searchTags}
          setSearchTags={setSearchTags}
          selectedPriorities={selectedPriorities}
          setSelectedPriorities={setSelectedPriorities}
          selectedModels={selectedModels}
          setSelectedModels={setSelectedModels}
          selectedUserDataKey={selectedUserDataKey}
          handleSelectUserDataKey={handleSelectUserDataKey}
          setSortCriteria={setSortCriteria}
          handleClearFilters={handleClearFilters}
          processModels={processModels}
          tagList={tagList}
          priorities={priorities}
          userDataKeys={userDataKeys}
          translations={translations}
          activeCount={activeFilterCount}
          selectedCreatorIds={selectedCreatorIds}
          setSelectedCreatorIds={setSelectedCreatorIds}
          orgUsers={orgUsers}
        />
      </Collapse>

      {liveProcessCanUse === false && !bypassSubscription && (
        <Alert
          id="mobile-live-process-exceeded"
          severity="error"
          sx={{ mt: 1, py: 0.5, fontSize: 11, alignItems: "center" }}
          action={
            organizationId && (
              <FlowentiButton
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(`/dashboard/organizations/${organizationId}/billing`, {
                    state: { organizationName },
                  })
                }
              >
                {upgradeLabel}
              </FlowentiButton>
            )
          }
        >
          {liveLimitLabel}
        </Alert>
      )}
    </Box>
  );
};

export default MyActivitiesMobileHeader;